import { useState, useEffect, useRef } from "react";
import apiService from "../config";

/* ─── Sub-components ─────────────────────────────────────────────── */

const Avatar = ({ name, image, size = "lg" }) => {
  const initials = name
    ? name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "?";
  const sizeClasses = { lg: "w-24 h-24 text-2xl", sm: "w-10 h-10 text-sm" };
  return (
    <div className={`${sizeClasses[size]} rounded-full flex items-center justify-center font-bold bg-gradient-to-br from-blue-400 to-blue-700 text-white shadow-lg overflow-hidden flex-shrink-0 ring-4 ring-white`}>
      {image ? <img src={image} alt={name} className="w-full h-full object-cover" /> : initials}
    </div>
  );
};

const Field = ({ label, value, icon }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[10px] font-bold uppercase tracking-widest text-blue-500">{label}</span>
    <div className="flex items-center gap-1.5">
      {icon && <span className="text-blue-300 text-xs">{icon}</span>}
      <span className="text-slate-700 font-medium text-sm">
        {value || <span className="text-slate-300 italic text-xs">Not set</span>}
      </span>
    </div>
  </div>
);

const InputField = ({ label, name, value, onChange, type = "text", disabled }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[10px] font-bold uppercase tracking-widest text-blue-400">{label}</label>
    <input
      type={type}
      name={name}
      value={value || ""}
      onChange={onChange}
      disabled={disabled}
      className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all outline-none
        ${disabled
          ? "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed"
          : "bg-white border-blue-200 text-slate-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
        }`}
    />
  </div>
);

/* ─── Profile Modal Content ──────────────────────────────────────── */

function ProfileModalContent({ onClose }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef();

  useEffect(() => { fetchProfile(); }, []);

  // Prevent body scroll when modal open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await apiService.getProfile();
      const data = res.data;
      setProfile(data);
      setFormData({
        full_name: data.full_name,
        email: data.email,
        mobile: data.mobile,
        address: data.address,
        designation: data.designation,
        department: data.department,
      });
    } catch {
      showToast("Failed to load profile", "error");
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      let payload;
      if (imageFile) {
        payload = new FormData();
        Object.entries(formData).forEach(([k, v]) => v && payload.append(k, v));
        payload.append("image", imageFile);
      } else {
        payload = formData;
      }
      await apiService.updateProfile(payload);
      setProfile((prev) => ({ ...prev, ...formData }));
      setEditing(false);
      showToast("Profile updated successfully!");
    } catch {
      showToast("Failed to update profile", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setImagePreview(null);
    setImageFile(null);
    setFormData({
      full_name: profile.full_name,
      email: profile.email,
      mobile: profile.mobile,
      address: profile.address,
      designation: profile.designation,
      department: profile.department,
    });
  };

  const joinDate = profile
    ? new Date(profile.cre_usr_dt).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })
    : "";

  const lastUpdated = profile
    ? new Date(profile.last_upd_dt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    : "";

  return (
    <>
      {/* Google Fonts */}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sora:wght@600;700&display=swap');`}</style>

      {/* Toast */}
      {toast && (
        <div className={`fixed top-5 right-5 z-[9999] px-4 py-3 rounded-xl shadow-lg text-sm font-semibold
          ${toast.type === "error" ? "bg-red-500 text-white" : "bg-green-600 text-white"}`}>
          {toast.message}
        </div>
      )}

      {/* Modal Overlay */}
      <div
        className="fixed inset-0 z-[9990] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          {/* Modal Header Banner */}
          <div className="h-24 bg-gradient-to-r from-blue-700 via-blue-600 to-blue-400 relative rounded-t-2xl flex-shrink-0">
            <div
              className="absolute inset-0 opacity-10 rounded-t-2xl"
              style={{
                backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)",
                backgroundSize: "18px 18px",
              }}
            />
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center transition-colors text-lg font-bold"
              aria-label="Close"
            >
              ✕
            </button>
            <div className="absolute top-3 left-4 text-white font-semibold text-sm opacity-80" style={{ fontFamily: "Sora, sans-serif" }}>
              User Profile
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-slate-400 text-sm">Loading profile…</span>
            </div>
          )}

          {/* Error State */}
          {!loading && !profile && (
            <div className="flex items-center justify-center py-20">
              <p className="text-slate-400">Could not load profile.</p>
            </div>
          )}

          {/* Profile Content */}
          {!loading && profile && (
            <div className="px-6 pb-6">
              {/* Avatar + Actions row */}
              <div className="flex items-end justify-between -mt-12 mb-5">
                <div className="relative group">
                  <Avatar name={profile.full_name} image={imagePreview || profile.image} size="lg" />
                  {editing && (
                    <>
                      <button
                        onClick={() => fileInputRef.current.click()}
                        className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-semibold"
                      >
                        Change
                      </button>
                      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                    </>
                  )}
                </div>

                {/* <div className="flex gap-2 pt-14">
                  {!editing ? (
                    <button
                      onClick={() => setEditing(true)}
                      className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors shadow-sm"
                    >
                      Edit Profile
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleCancel}
                        className="px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 text-sm font-semibold transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold transition-colors shadow-sm min-w-[76px]"
                      >
                        {saving ? "Saving…" : "Save"}
                      </button>
                    </>
                  )}
                </div> */}
              </div>

              {/* Name / Title */}
              {!editing ? (
                <div className="mb-5">
                  <h1 className="text-xl font-bold text-slate-800" style={{ fontFamily: "Sora, sans-serif" }}>
                    {profile.full_name}
                  </h1>
                  <p className="text-blue-600 font-semibold text-sm mt-0.5">{profile.designation}</p>
                  <p className="text-slate-400 text-sm">{profile.user_role_name}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                  <InputField label="Full Name" name="full_name" value={formData.full_name} onChange={handleChange} />
                  <InputField label="Designation" name="designation" value={formData.designation} onChange={handleChange} />
                </div>
              )}

              {/* Info Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                {/* Contact */}
                <div className="bg-blue-50 rounded-xl p-4 space-y-3 border border-blue-100">
                  <h2 className="text-[10px] font-bold uppercase tracking-widest text-blue-700 border-b border-blue-200 pb-1.5">
                    Contact Info
                  </h2>
                  {!editing ? (
                    <>
                      <Field label="Email" value={profile.email} icon="✉" />
                      <Field label="Mobile" value={profile.mobile} icon="📱" />
                      {/* <Field label="Address" value={profile.address} icon="📍" /> */}
                    </>
                  ) : (
                    <>
                      <InputField label="Email" name="email" type="email" value={formData.email} onChange={handleChange} />
                      <InputField label="Mobile" name="mobile" value={formData.mobile} onChange={handleChange} />
                      <InputField label="Address" name="address" value={formData.address} onChange={handleChange} />
                    </>
                  )}
                </div>

                {/* Account */}
                <div className="bg-blue-50 rounded-xl p-4 space-y-3 border border-blue-100">
                  <h2 className="text-[10px] font-bold uppercase tracking-widest text-blue-700 border-b border-blue-200 pb-1.5">
                    Account Details
                  </h2>
                  {/* <Field label="User ID" value={`#${profile.usr_id}`} /> */}
                  <Field label="Role" value={profile.user_role_name} />
                  <Field
                    label="Status"
                    value={
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold
                        ${profile.is_active ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-500"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${profile.is_active ? "bg-emerald-500" : "bg-red-400"}`} />
                        {profile.is_active ? "Active" : "Inactive"}
                      </span>
                    }
                  />
                  {/* <Field label="Member Since" value={joinDate} /> */}
                </div>
              </div>

              {/* Project & Department */}
              {/* <div className="bg-blue-50 rounded-xl p-4 space-y-3 border border-blue-100">
                <h2 className="text-[10px] font-bold uppercase tracking-widest text-blue-700 border-b border-blue-200 pb-1.5">
                  Project & Department
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {!editing ? (
                    <>
                      <Field label="Department" value={profile.department} />
                      <Field label="Default Project" value={profile.default_project_name} />
                      <Field label="District" value={profile.district} />
                      <Field label="Taluka" value={profile.taluka} />
                    </>
                  ) : (
                    <>
                      <InputField label="Department" name="department" value={formData.department} onChange={handleChange} />
                      <InputField label="District" name="district" value={formData.district} onChange={handleChange} disabled />
                      <InputField label="Taluka" name="taluka" value={formData.taluka} onChange={handleChange} disabled />
                    </>
                  )}
                </div>
              </div> */}

              <p className="text-center text-[10px] text-slate-300 mt-4">Last updated: {lastUpdated}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ─── Main Export: Modal with Trigger ───────────────────────────── */

export default function UserProfile({ onClose }) {
  return <ProfileModalContent onClose={onClose} />;
}
